import { Injectable, Optional, Host } from '@angular/core';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { DateRange, DynamicRange, Weekday, DateRangeValue, FixedDateRange, DynamicDateRange } from './types';
import { SessionService } from '../../services/session/session.service';

export class BaseDate {
  date: Date;
}
declare global {
  interface Date {
    addQuarters: (offset: number) => Date
    beginningOfQuarter: () => Date
  }
}

Date.prototype.addQuarters = function (offset) {
  return this.addMonths(offset * 3);
};

Date.prototype.beginningOfQuarter = function () {
  const month = Math.floor(this.getMonth() / 3) * 3;
  const year = this.getFullYear();
  return this.setFullYear(year, month, 1);
};

@Injectable()
export class DatesService {
  readonly base: Date;
  readonly validUnits = /^(day|week|month|quarter|year)s?$/;
  readonly accountFirstDayOfWeek: Weekday;

  constructor(@Host() @Optional() base: BaseDate, @Optional() sessionService: SessionService) {
    const options = sessionService?.account?.options;
    const timezone = (options?.timezone || 'eu') === 'eu' ? 'Europe/Stockholm' : 'America/New_York';
    this.base = base?.date || utcToZonedTime(new Date(), timezone);
    this.accountFirstDayOfWeek = (options && Number(options.first_day_of_week) === parseInt(options.first_day_of_week) && options.first_day_of_week >= 0) ? Number(options.first_day_of_week) % 7 : Weekday.Sunday;
  }

  public isDynamic(dateRange: DateRangeValue): dateRange is DynamicDateRange {
    return dateRange['dynamic'];
  }

  beginningOfWeek(date: Date, queryFirstDayOfWeek?: Weekday): Date {
    const firstDayOfWeek = queryFirstDayOfWeek === undefined ? this.accountFirstDayOfWeek : queryFirstDayOfWeek;
    return date.addDays(-((date.getWeekday() + 7 - firstDayOfWeek) % 7)).beginningOfDay();
  }

  interval(dynamicRange: DynamicRange, queryFirstDayOfWeek?: Weekday): string {
    return this.fixedQueryInterval(this.parse(dynamicRange, queryFirstDayOfWeek));
  }

  public intervalDateRange(dateRange: DateRangeValue, queryFirstDayOfWeek?: Weekday): string {
    const date = this.parseValue(dateRange, queryFirstDayOfWeek);
    const startDate = format(date.startDate, 'yyyy-MM-dd');
    const endDate = format(date.endDate, 'yyyy-MM-dd');
    return `${startDate} - ${endDate}`;
  }

  fixedQueryInterval({ startDate, endDate }: DateRange): string {
    return `${startDate.format('{yyyy}{MM}{dd}')}-${endDate.format('{yyyy}{MM}{dd}')}`;
  }

  parseSingleDateValue(value: string): Date {
    const date = parseInt(value.replace(/-/g, ''), 10);
    return new Date(date / 10000, (date % 10000 / 100) - 1, date % 100);
  }

  parseValue(value: DateRangeValue, queryFirstDayOfWeek?: Weekday): DateRange {
    return this.isDynamic(value) ? this.parse(value['dynamic'], queryFirstDayOfWeek) : value;
  }

  parse(dynamicRange: DynamicRange, queryFirstDayOfWeek?: Weekday): DateRange {
    const base = this.base.clone();
    switch (dynamicRange.name) {
      case 'today':
        return this.today(base);
      case 'yesterday':
        return this.yesterday(base);
      case 'last':
        return this.parseRelative(base, dynamicRange, true, queryFirstDayOfWeek);
      case 'this':
        return this.parseRelative(base, dynamicRange, false, queryFirstDayOfWeek);
    }
    throw new Error('Unable to parse dynamic date');
  }

  intervalToFixedDateRange(interval: string): FixedDateRange {
    const [startDate, endDate] = interval.split('-').map(dateStr => {
      const date = parseInt(dateStr, 10);
      return new Date(date / 10000, (date % 10000 / 100) - 1, date % 100);
    });
    return {
      type: 'fixed',
      startDate,
      endDate,
      range: 'Fixed',
    };
  }

  private parseRelative(d: Date, dynamicRange: DynamicRange, last: boolean, queryFirstDayOfWeek?: Weekday): DateRange {
    d = d.clone();
    let unit = dynamicRange.unit;
    let num = dynamicRange.num || 1;
    const today = d.clone();

    if (unit) {
      const matches = unit.match(this.validUnits);
      if (matches) {
        unit = <any> matches[1];
        if (num) {
          let offsetFn: string;
          if (unit === 'week') {
            this.beginningOfWeek(d, queryFirstDayOfWeek);
            num = 7 * num;
            offsetFn = 'addDays';
            if (!last) {
              d.addDays(7);
            }
          } else {
            offsetFn = `add${unit.titleize()}s`;
            d[`beginningOf${unit.titleize()}`]();
            if (!last) {
              d[offsetFn](1);
            }
          }
          let endDate = d.clone().addDays(-1);
          d[offsetFn](-num);

          if (dynamicRange.toDate && this.supportsToDate(last, unit)) {
            endDate = today;
          }
          return this.toDateRange(d, endDate);
        }
      }
    }
    throw new Error('Unable to parse dynamic date');
  }

  private supportsToDate(last: boolean, unit: string): boolean {
    if (!last && (unit === 'month' || unit === 'quarter' || unit === 'year')) {
      return true;
    } else {
      return false;
    }
  }

  private yesterday(d: Date): DateRange {
    const s = d.clone().addDays(-1);
    return this.toDateRange(s, s);
  }

  private today(d): DateRange {
    const s = d.clone();
    return this.toDateRange(s, s);
  }

  private toDateRange(s: Date, e: Date): DateRange {
    return { startDate: s, endDate: e };
  }
}
